import type { SelectedCartFeature } from "@/lib/product-features";
import { BranchStatusData, CartData } from "./cartApi";

const getBaseUrl = () => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) throw new Error("Missing backend URL");
  //return `${supabaseUrl}/functions/v1`;
  return `${window.location.origin}/api/WebOrder/functions/v1`;
};

const getHeaders = () => {
  const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  if (!anonKey) throw new Error("Missing publishable key");
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    apikey: anonKey,
    Authorization: `Bearer ${anonKey}`,
  };
};

export interface CartMutationResult {
  success: boolean;
  cartData: CartData | null;
  branchStatus?: BranchStatusData | null;
  error: { code: string; message: string } | null;
}

async function callCartItems(
  body: Record<string, unknown>,
): Promise<CartMutationResult> {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10_000);

    const response = await fetch(`${getBaseUrl()}/cart-items`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    clearTimeout(timeout);
    const json = await response.json();
    const branchStatus = json.data?.branch_status ?? json.branch_status ?? null;

    if (response.ok && json.success) {
      return { success: true, cartData: json.data, branchStatus, error: null };
    }

    return {
      success: false,
      cartData: null,
      branchStatus,
      error: json.error || { code: "UNKNOWN", message: "Unexpected response" },
    };
  } catch (err: any) {
    return {
      success: false,
      cartData: null,
      error: {
        code: err.name === "AbortError" ? "TIMEOUT" : "NETWORK_ERROR",
        message: err.message || "Network request failed",
      },
    };
  }
}

export async function addCartItem(
  cartId: string,
  item: {
    item_id: string;
    name: string;
    name_ar?: string;
    quantity: number;
    unit_price: number;
    size?: string | null;
    extras?: string[];
    removed_ingredients?: string[];
    selected_features?: SelectedCartFeature[];
    special_instructions?: string;
    geolocation_coordinates: { latitude: number; longitude: number } | null;
    distance_km?: number | 0,
    phone_number?: string | null;
  },
  order_type?: string,
  restaurant_id?: string,
  branch_id?: string | null,
): Promise<CartMutationResult> {
  return callCartItems({ action: "add", cart_id: cartId, order_type, restaurant_id, branch_id, ...item });
}

export async function removeCartItem(
  cartId: string,
  itemId: string,
  order_type?: string,
  restaurant_id?: string,
  branch_id?: string | null,
  geolocation_coordinates?: { latitude: number; longitude: number } | null,
  distance_km?: number | 0,
  phone_number?: string | null
): Promise<CartMutationResult> {
  return callCartItems({ action: "remove", cart_id: cartId, item_id: itemId, order_type, restaurant_id, branch_id, geolocation_coordinates, distance_km, phone_number });
}

export async function updateCartItemQuantity(
  cartId: string,
  itemId: string,
  quantity: number,
  order_type?: string,
  restaurant_id?: string,
  branch_id?: string | null,
  geolocation_coordinates?: { latitude: number; longitude: number } | null,
  distance_km?: number | 0,
  phone_number?: string | null
): Promise<CartMutationResult> {
  return callCartItems({ action: "update", cart_id: cartId, item_id: itemId, quantity, order_type, restaurant_id, branch_id,geolocation_coordinates,distance_km, phone_number });
}

export async function clearCartItems(
  cartId: string,
  order_type?: string,
  restaurant_id?: string,
  branch_id?: string | null,
  phone_number?: string | null
): Promise<CartMutationResult> {
  const result = await callCartItems({ action: "clear", cart_id: cartId, order_type, restaurant_id, branch_id ,phone_number});
  if (!result.success && result.error?.code === "CART_INVALID") {
    return { success: true, cartData: null, error: null };
  }
  return result;
}
